import React from 'react'
import { FieldError, FieldHint, FieldLabel, FieldWrap } from './Field.styles'

type ControlProps = React.HTMLAttributes<HTMLElement> & { id?: string }

interface FieldProps {
	/** Wires the label to the control and names the hint / error for assistive tech. */
	id: string
	label: string
	/** Helper copy under the control; hidden while an error shows. */
	hint?: string
	/** Validation message; also marks the control invalid. */
	error?: string
	/** One control — an `Input`, `Textarea`, `Dropdown`, or any element that takes an id. */
	children: React.ReactElement<ControlProps>
}

/**
 * Label, control and message as one object, so every field meets the
 * accessible-name rule by construction: the label points at the control's id,
 * and the hint or error is announced through `aria-describedby`.
 */
const Field: React.FC<FieldProps> = ({ id, label, hint, error, children }) => {
	const hintId = hint && !error ? `${id}-hint` : undefined
	const errorId = error ? `${id}-error` : undefined
	const control = React.cloneElement(children, {
		id,
		'aria-invalid': error ? true : undefined,
		'aria-describedby': [hintId, errorId].filter(Boolean).join(' ') || undefined,
	})

	return (
		<FieldWrap>
			<FieldLabel htmlFor={id}>{label}</FieldLabel>
			{control}
			{hintId && <FieldHint id={hintId}>{hint}</FieldHint>}
			{errorId && (
				<FieldError id={errorId} role="alert">
					{error}
				</FieldError>
			)}
		</FieldWrap>
	)
}

export default Field
